import { useEffect, useRef } from 'react';
import { MailToolbar } from './MailToolbar';
import { MailListItem } from './MailListItem';
import { SkeletonBox, SkeletonText } from '@/components';

const FALLBACK_CATEGORIES = [
  { id: 'primary', label: 'Primary', count: 0, icon: 'mail' },
  { id: 'promotions', label: 'Promotions', count: 0, icon: 'tag' },
  { id: 'social', label: 'Social', count: 0, icon: 'users' },
  { id: 'updates', label: 'Updates', count: 0, icon: 'info' },
];

const SKELETON_ROWS = [
  { sender: 'w-24 sm:w-32', subject: 'w-32 sm:w-40', snippet: 'w-3/4' },
  { sender: 'w-28 sm:w-36', subject: 'w-44 sm:w-56', snippet: 'w-4/5' },
  { sender: 'w-20 sm:w-28', subject: 'w-28 sm:w-36', snippet: 'w-2/3' },
  { sender: 'w-32 sm:w-40', subject: 'w-48 sm:w-60', snippet: 'w-5/6' },
  { sender: 'w-24 sm:w-32', subject: 'w-36 sm:w-48', snippet: 'w-3/4' },
  { sender: 'w-28 sm:w-36', subject: 'w-40 sm:w-52', snippet: 'w-4/5' },
  { sender: 'w-20 sm:w-28', subject: 'w-32 sm:w-44', snippet: 'w-3/5' },
  { sender: 'w-32 sm:w-40', subject: 'w-48 sm:w-64', snippet: 'w-4/5' },
  { sender: 'w-24 sm:w-32', subject: 'w-36 sm:w-48', snippet: 'w-2/3' },
  { sender: 'w-28 sm:w-36', subject: 'w-40 sm:w-52', snippet: 'w-3/4' },
];

function MailListItemSkeleton({ config = SKELETON_ROWS[0] }) {
  return (
    <div className="flex items-center gap-2.5 sm:gap-3 px-3 sm:px-4 py-2.5 min-h-[52px] border-b border-gray-100 bg-white select-none">
      {/* Indicator placeholder */}
      <SkeletonBox className="w-2 h-2 shrink-0" rounded="rounded-full" />

      {/* Avatar Circle */}
      <SkeletonBox className="w-10 h-10 shrink-0" rounded="rounded-full" />

      {/* Sender Name */}
      <div className="w-36 sm:w-44 lg:w-52 shrink-0">
        <SkeletonText width={config.sender} height="h-3.5" />
      </div>

      {/* Subject & Snippet */}
      <div className="flex-1 min-w-0 flex items-center gap-2">
        <SkeletonText
          width={config.subject}
          height="h-3.5"
          className="shrink-0"
        />
        <SkeletonText width={config.snippet} height="h-3" className="flex-1" />
      </div>

      {/* Date */}
      <div className="shrink-0 ml-3 min-w-[65px] flex justify-end">
        <SkeletonText width="w-12" height="h-3" />
      </div>
    </div>
  );
}

export function MailList({
  conversations = [],
  isLoading = false,
  activeConversationId = null,
  onSelectConversation,
  searchQuery = '',
  onSearchChange,
  onRefresh,
  onNextPage,
  hasNextPage,
  isFetchingNextPage,
  totalApiCount,
  syncInfo = null,
  onStartSync,
  activeCategory,
  onCategoryChange,
  categories = [],
  activeFolder,
  activeLabel,
}) {
  const sentinelRef = useRef(null);

  // IntersectionObserver: auto-load next page when scrolling to the bottom
  useEffect(() => {
    const el = sentinelRef.current;
    if (!el) return;
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && hasNextPage && !isFetchingNextPage) {
          onNextPage?.();
        }
      },
      { threshold: 0.1 },
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, [hasNextPage, isFetchingNextPage, onNextPage, isLoading]);

  const renderCategoryIcon = (iconName) => {
    switch (iconName) {
      case 'mail':
        return (
          <svg
            className="w-4 h-4"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
            strokeWidth="2"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"
            />
          </svg>
        );
      case 'tag':
        return (
          <svg
            className="w-4 h-4"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
            strokeWidth="2"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              d="M7 7h.01M7 3h5c.512 0 1.024.195 1.414.586l7 7a2 2 0 010 2.828l-7 7a2 2 0 01-2.828 0l-7-7A1.994 1.994 0 013 12V7a4 4 0 014-4z"
            />
          </svg>
        );
      case 'users':
        return (
          <svg
            className="w-4 h-4"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
            strokeWidth="2"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z"
            />
          </svg>
        );
      case 'info':
        return (
          <svg
            className="w-4 h-4"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
            strokeWidth="2"
          >
            <circle cx="12" cy="12" r="10" />
            <line x1="12" y1="16" x2="12" y2="12" />
            <line x1="12" y1="8" x2="12.01" y2="8" />
          </svg>
        );
      default:
        return null;
    }
  };

  return (
    <div className="flex flex-col h-full w-full bg-white select-none overflow-hidden">
      {/* Search Bar */}
      <div className="p-4 pb-3 border-b border-gray-100">
        <div className="relative flex items-center">
          <svg
            className="w-4 h-4 text-gray-400 absolute left-3.5 pointer-events-none"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
            strokeWidth="2"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
            />
          </svg>
          <input
            type="text"
            value={searchQuery}
            onChange={(e) => onSearchChange?.(e.target.value)}
            placeholder="Search mail, contacts or messages..."
            className="w-full h-10 pl-10 pr-4 bg-gray-50 hover:bg-gray-100/70 focus:bg-white text-xs font-medium text-gray-800 placeholder-gray-400 rounded-xl border border-gray-200 focus:border-primary focus:ring-1 focus:ring-primary outline-none transition-all"
          />
        </div>
      </div>

      {/* Category Tabs */}
      {activeFolder === 'inbox' && !activeLabel && (
        <div className="flex items-center border-b border-gray-100 px-4 gap-6 bg-white overflow-x-auto">
          {(categories?.categories?.length
            ? categories.categories
            : FALLBACK_CATEGORIES
          ).map((cat) => {
            const isActive = activeCategory === cat.id;
            return (
              <button
                key={cat.id}
                type="button"
                onClick={() => onCategoryChange?.(cat.id)}
                className={`flex items-center gap-2 py-3 px-1 text-xs font-semibold border-b-2 transition-all shrink-0 ${
                  isActive
                    ? 'border-primary text-primary font-bold'
                    : 'border-transparent text-gray-500 hover:text-gray-800'
                }`}
              >
                <span>{renderCategoryIcon(cat.icon)}</span>
                <span>{cat.label}</span>
                {cat.count > 0 && (
                  <span
                    className={`px-1.5 py-0.5 rounded-full text-[10px] ${
                      isActive
                        ? 'bg-surface-mint text-primary font-bold'
                        : 'bg-gray-100 text-gray-500'
                    }`}
                  >
                    {cat.count}
                  </span>
                )}
              </button>
            );
          })}
        </div>
      )}

      {/* Toolbar */}
      <MailToolbar
        totalCount={conversations.length}
        onRefresh={onRefresh}
        hasNextPage={hasNextPage}
        isFetchingNextPage={isFetchingNextPage}
        totalApiCount={totalApiCount}
        syncInfo={syncInfo}
        onStartSync={onStartSync}
      />

      {/* Mail Rows Container */}
      <div className="flex-1 overflow-y-auto divide-y divide-gray-50">
        {isLoading ? (
          <div>
            {SKELETON_ROWS.map((config, idx) => (
              <MailListItemSkeleton key={idx} config={config} />
            ))}
          </div>
        ) : conversations.length === 0 ? (
          <div className="flex flex-col items-center justify-center h-64 text-gray-400 text-xs">
            <svg
              className="w-10 h-10 mb-2 text-gray-300"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth="1.5"
                d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"
              />
            </svg>
            <span>No emails found in this view.</span>
          </div>
        ) : (
          <>
            {conversations.map((item) => (
              <MailListItem
                key={item.id}
                item={item}
                isSelected={activeConversationId === item.id}
                onClickRow={() => onSelectConversation?.(item)}
              />
            ))}
            {/* Sentinel element for infinite scroll */}
            <div ref={sentinelRef} className="w-full shrink-0 min-h-[20px]">
              {isFetchingNextPage && (
                <div>
                  {[0, 1, 2].map((idx) => (
                    <MailListItemSkeleton
                      key={`skel-more-${idx}`}
                      config={SKELETON_ROWS[idx]}
                    />
                  ))}
                </div>
              )}
            </div>
          </>
        )}
      </div>
    </div>
  );
}
